import React, { useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { loginService } from "../../services/loginService";
import { LoginCredentialsDTO } from "../../interfaces/DTO/auth";
import { isServerUnavailableError } from "../../core/serverHealth";

interface LoginFormProps {
  onSuccess: () => void; // פעולה לביצוע לאחר התחברות מוצלחת
  onServerDown?: () => void; // השרת לא זמין (502/503/504 או שגיאת רשת) — לא שגיאת פרטי התחברות
  onLoginFailed?: () => void; // ניסיון נכשל בגלל פרטי התחברות
  resubmitSignal?: number; // כל שינוי בערך שולח שוב את הטופס עם מה שכבר הוקלד (אחרי שהשרת חזר)
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onServerDown, onLoginFailed, resubmitSignal }) => {
  const { register, handleSubmit } = useForm<LoginCredentialsDTO>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // שמירת הודעת השגיאה
  const [showPassword, setShowPassword] = useState<boolean>(false); // שליטה על הצגת הסיסמה

  const submittingRef = useRef(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const onSubmit: SubmitHandler<LoginCredentialsDTO> = async (data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await loginService.loginUser(data); // קריאה לשירות ההתחברות
      setErrorMessage(null); // איפוס הודעת השגיאה במקרה של הצלחה
      onSuccess();
    } catch (error) {
      console.error("Login failed:", error);
      if (isServerUnavailableError(error) && onServerDown) {
        setErrorMessage(null);
        onServerDown();
        return;
      }
      setErrorMessage("Login failed. Please check your credentials."); // הגדרת הודעת השגיאה
      onLoginFailed?.();
    } finally {
      submittingRef.current = false;
    }
  };

  useEffect(() => {
    if (resubmitSignal) handleSubmit(onSubmit)();
  }, [resubmitSignal]);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
      <TextField
        label="Username"
        {...register("username", { required: true })}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Password"
        {...register("password", { required: true })}
        type={showPassword ? "text" : "password"} // שינוי הסוג בין טקסט לסיסמה
        fullWidth
        margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Company ID"
        {...register("company_id", { required: true })}
        type="number"
        fullWidth
        margin="normal"
      />
      <Button type="submit" variant="contained" color="primary" fullWidth>
        Login
      </Button>
      {errorMessage && ( // הצגת הודעת השגיאה אם קיימת
        <Typography
          color="error"
          variant="body2"
          sx={{ mt: 2, textAlign: "center" }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default LoginForm;
